import React from 'react'
import SEOHead from '@/components/SEO/SEOHead'
import { buildGraph, breadcrumbListData, serviceSchema, faqData } from '@/data/structured-data'
import PageHeader from '@/components/Common/PageHeader'
import { contactInfo } from '@/data/contact'
import { toTelHref, toWhatsAppHref } from '@/utils/phone'
import ArticleBody from '@/components/Content/ArticleBody'
import { acilContent } from '@/data/landing-content'
import { neighborhoods } from '@/data/neighborhoods'
import { Link } from 'react-router-dom'

const AcilVeterinerPage: React.FC = () => {
  const telHref = toTelHref(contactInfo.phone)
  const waHref = toWhatsAppHref(contactInfo.whatsapp)

  return (
    <>
      <SEOHead
        title="Acil Veteriner Şanlıurfa | 7/24 Açık"
        description="Şanlıurfa'da 7/24 acil veteriner desteği. Şok yönetimi, triiyaj ve hızlı stabilizasyon. Hemen arayın veya WhatsApp'tan yazın."
        keywords="acil veteriner, 7 24 veteriner, gece açık veteriner, nöbetçi veteriner şanlıurfa, şanlıurfa veteriner"
        canonicalUrl="/acil-veteriner"
        structuredData={buildGraph(
          serviceSchema({
            name: 'Acil Veteriner Hizmeti',
            description: "Şanlıurfa'da 7/24 acil veteriner desteği. Şok yönetimi, triyaj ve hızlı stabilizasyon.",
            url: '/acil-veteriner',
            serviceType: 'Acil veteriner müdahalesi'
          }),
          faqData(acilContent.faqs),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: 'Acil Veteriner', url: '/acil-veteriner' }
          ])
        )}
      />
      <PageHeader
        title="Acil Veteriner"
        subtitle="7/24 açığız. Yola çıkmadan önce arayın — siz gelene kadar hazırlık yapalım."
      />

      {/* Acil çağrı şeridi: sayfanın en üstünde, tek dokunuşla arama */}
      <section className="bg-red-600 text-white py-4">
        <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-semibold text-center sm:text-left">
            Acil bir durum mu var? Beklemeyin, hemen arayın.
          </p>
          <div className="flex gap-3">
            <a href={telHref} className="bg-white text-red-700 font-bold px-5 py-2 rounded-lg hover:bg-red-50 transition-colors">
              {contactInfo.phone}
            </a>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-white/60 px-5 py-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom grid grid-cols-1 lg:grid-cols-3 gap-10">
          <article className="lg:col-span-2">
            <ArticleBody
              intro={acilContent.intro}
              sections={acilContent.sections}
              faqs={acilContent.faqs}
              disclaimer="Bu sayfa bilgilendirme amaçlıdır ve muayene yerine geçmez. Acil bir durumdan şüpheleniyorsanız internetten araştırma yapmak yerine doğrudan arayın."
            />
          </article>

          <aside className="space-y-6">
            <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-6 lg:sticky lg:top-24">
              <h3 className="font-bold text-red-900 mb-2">7/24 acil hat</h3>
              <p className="text-sm text-red-800 mb-4">
                Gece, hafta sonu ve resmî tatillerde de ulaşabilirsiniz.
              </p>
              <a href={telHref} className="btn-primary w-full inline-flex items-center justify-center bg-red-600 hover:bg-red-700">
                {contactInfo.phone}
              </a>
              <p className="text-xs text-red-700 mt-3">{contactInfo.address}</p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-3">Hızlı ulaşım</h3>
              <p className="text-sm text-gray-600 mb-3">
                Karaköprü mahallelerinden kliniğe tahmini süre:
              </p>
              <ul className="space-y-1.5 text-sm">
                {[...neighborhoods].sort((a, b) => a.driveMinutes - b.driveMinutes).slice(0, 8).map(n => (
                  <li key={n.slug} className="flex items-center justify-between gap-2">
                    <Link to={`/karakopru-veteriner/${n.slug}`} className="text-primary-700 hover:underline">
                      {n.name}
                    </Link>
                    <span className="text-gray-500 whitespace-nowrap">{n.driveMinutes} dk</span>
                  </li>
                ))}
              </ul>
              <Link to="/karakopru-veteriner" className="text-sm font-semibold text-primary-700 hover:underline mt-3 inline-block">
                Tüm mahalleler →
              </Link>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-3">İlgili sayfalar</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/kedi" className="text-primary-700 hover:underline">Kedi sağlığı</Link></li>
                <li><Link to="/kopek" className="text-primary-700 hover:underline">Köpek sağlığı</Link></li>
                <li><Link to="/rontgen-fiyatlari" className="text-primary-700 hover:underline">Röntgen ve görüntüleme</Link></li>
                <li><Link to="/hizmetler/cerrahi" className="text-primary-700 hover:underline">Cerrahi</Link></li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  )
}

export default AcilVeterinerPage


